
import { useState } from "react";
import {
  Info,
  Server,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Globe,
  Radio
} from "lucide-react";
import type { Finding, ScanResultsData } from "./types";

interface OverviewTabProps {
  data: ScanResultsData;
  flatFindings: Finding[];
  severityStats: {
    CRITICAL: number;
    HIGH: number;
    MEDIUM: number;
    LOW: number;
    INFO: number;
  };
  onSeverityClick: (severity: string) => void;
}

export default function OverviewTab({
  data,
  flatFindings,
  severityStats,
  onSeverityClick,
}: OverviewTabProps) {
  const [showCalculationDetails, setShowCalculationDetails] = useState(false);

  // 1. Calculate Security Health Score
  const calculateHealthScore = () => {
    let score = 100;

    // Deduct for vulnerabilities
    score -= severityStats.CRITICAL * 20;
    score -= severityStats.HIGH * 12;
    score -= severityStats.MEDIUM * 7;
    score -= severityStats.LOW * 3;

    // Deduct for missing security headers
    const fingerprint = data.server_fingerprint?.[0];
    if (fingerprint && fingerprint.security) {
      const sec = fingerprint.security;
      const headers = ["hsts", "csp", "frame_options", "content_type_options", "xss_protection"];
      headers.forEach((h) => {
        if (sec[h as keyof typeof sec] === "missing") {
          score -= 5;
        }
      });
    }

    return Math.max(10, score);
  };

  const score = calculateHealthScore();

  // Get Letter Grade and Colors
  const getGradeDetails = (scoreVal: number) => {
    if (scoreVal >= 90) return { grade: "A", color: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-500/10", border: "border-emerald-200 dark:border-emerald-500/20", label: "Excellent" };
    if (scoreVal >= 80) return { grade: "B", color: "text-green-600 dark:text-green-400", bg: "bg-green-50 dark:bg-green-500/10", border: "border-green-200 dark:border-green-500/20", label: "Good" };
    if (scoreVal >= 70) return { grade: "C", color: "text-yellow-600 dark:text-yellow-400", bg: "bg-yellow-50 dark:bg-yellow-500/10", border: "border-yellow-200 dark:border-yellow-500/20", label: "Fair" };
    if (scoreVal >= 50) return { grade: "D", color: "text-orange-600 dark:text-orange-400", bg: "bg-orange-50 dark:bg-orange-500/10", border: "border-orange-200 dark:border-orange-500/20", label: "Weak" };
    return { grade: "F", color: "text-red-600 dark:text-red-400", bg: "bg-red-50 dark:bg-red-500/10", border: "border-red-200 dark:border-red-500/20", label: "Critical Risk" };
  };

  const gradeInfo = getGradeDetails(score);

  // 2. Stacked progress bar breakdown
  const totalFindings = flatFindings.length;
  const getPercent = (count: number) => (totalFindings > 0 ? (count / totalFindings) * 100 : 0);

  // 3. Header compliance checklist
  const getHeaderCompliance = () => {
    const fingerprint = data.server_fingerprint?.[0];
    const sec = fingerprint?.security || {};
    return [
      {
        key: "hsts",
        name: "HSTS",
        fullName: "Strict-Transport-Security",
        description: "Prevents SSL-stripping and cookie hijacking.",
        status: sec.hsts !== "missing" && !!sec.hsts,
      },
      {
        key: "csp",
        name: "CSP",
        fullName: "Content-Security-Policy",
        description: "Mitigates XSS and content injection attacks.",
        status: sec.csp !== "missing" && !!sec.csp,
      },
      {
        key: "frame_options",
        name: "X-Frame-Options",
        fullName: "X-Frame-Options",
        description: "Prevents framing and clickjacking attacks.",
        status: sec.frame_options !== "missing" && !!sec.frame_options,
      },
      {
        key: "content_type_options",
        name: "MIME Sniffing",
        fullName: "X-Content-Type-Options",
        description: "Prevents MIME-type sniffing vulnerabilities.",
        status: sec.content_type_options !== "missing" && !!sec.content_type_options,
      },
      {
        key: "xss_protection",
        name: "XSS Filter",
        fullName: "X-XSS-Protection",
        description: "Enforces legacy browser XSS filters.",
        status: sec.xss_protection !== "missing" && !!sec.xss_protection,
      },
    ];
  };

  const headers = getHeaderCompliance();

  return (
    <div className="space-y-6">
      {/* Dynamic Summary Dashboard Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Visual Security Grade Card */}
        <div className="bg-gray-50/70 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-800 rounded-xl p-5 flex flex-col justify-between shadow-sm relative overflow-hidden group min-h-[160px]">
          <div className="absolute -right-6 -bottom-6 text-gray-300/30 dark:text-gray-800/10 group-hover:scale-110 transition-transform duration-300">
            <ShieldCheck size={120} />
          </div>

          <div className="flex items-center justify-between w-full relative z-10">
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none">Security Health Rating</p>
              <h3 className={`text-xl font-black ${gradeInfo.color} tracking-tight`}>{gradeInfo.label}</h3>
              <p className="text-xs text-gray-600 dark:text-gray-300 font-medium">
                Based on {totalFindings} findings and configurations.
              </p>
              <div className="pt-2 flex items-center gap-1.5 flex-wrap">
                <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${gradeInfo.bg} ${gradeInfo.color} border ${gradeInfo.border}`}>
                  Score: {score}/100
                </span>
                <button
                  onClick={() => setShowCalculationDetails(!showCalculationDetails)}
                  className="text-[10px] font-bold text-orange-600 dark:text-orange-500 hover:text-orange-500 dark:hover:text-orange-400 transition-colors flex items-center gap-0.5 underline hover:no-underline cursor-pointer"
                >
                  <Info size={11} />
                  {showCalculationDetails ? "Hide calculation" : "How is this calculated?"}
                </button>
              </div>
            </div>

            <div className="relative flex items-center justify-center w-24 h-24 shrink-0">
              {/* SVG circle track */}
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="38"
                  className="stroke-gray-200 dark:stroke-gray-800"
                  strokeWidth="6"
                  fill="transparent"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="38"
                  className={score >= 70 ? "stroke-emerald-500" : score >= 50 ? "stroke-orange-500" : "stroke-red-500"}
                  strokeWidth="6"
                  fill="transparent"
                  strokeDasharray="238.76"
                  strokeDashoffset={238.76 - (238.76 * score) / 100}
                  strokeLinecap="round"
                />
              </svg>
              <span className={`absolute text-3xl font-black font-mono tracking-tighter ${gradeInfo.color}`}>
                {gradeInfo.grade}
              </span>
            </div>
          </div>

          {/* Collapsible Calculation Details */}
          {showCalculationDetails && (
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-800/80 relative z-10 text-[10px] space-y-1.5 font-mono text-gray-600 dark:text-gray-300">
              <div className="flex justify-between text-gray-800 dark:text-gray-300 font-bold uppercase tracking-wider text-[9px] pb-1">
                <span>Deduction Category</span>
                <span>Impact</span>
              </div>
              <div className="flex justify-between">
                <span>Base Score:</span>
                <span className="text-gray-900 dark:text-gray-200 font-bold">100</span>
              </div>
              {severityStats.CRITICAL > 0 && (
                <div className="flex justify-between text-red-600 dark:text-red-400">
                  <span>Critical Findings (-20 ea × {severityStats.CRITICAL}):</span>
                  <span>-{severityStats.CRITICAL * 20}</span>
                </div>
              )}
              {severityStats.HIGH > 0 && (
                <div className="flex justify-between text-orange-600 dark:text-orange-400">
                  <span>High Findings (-12 ea × {severityStats.HIGH}):</span>
                  <span>-{severityStats.HIGH * 12}</span>
                </div>
              )}
              {severityStats.MEDIUM > 0 && (
                <div className="flex justify-between text-yellow-600 dark:text-yellow-400">
                  <span>Medium Findings (-7 ea × {severityStats.MEDIUM}):</span>
                  <span>-{severityStats.MEDIUM * 7}</span>
                </div>
              )}
              {severityStats.LOW > 0 && (
                <div className="flex justify-between text-blue-600 dark:text-blue-400">
                  <span>Low Findings (-3 ea × {severityStats.LOW}):</span>
                  <span>-{severityStats.LOW * 3}</span>
                </div>
              )}
              {(() => {
                const fingerprint = data.server_fingerprint?.[0];
                let missingHeadersCount = 0;
                if (fingerprint && fingerprint.security) {
                  const sec = fingerprint.security;
                  const headersList = ["hsts", "csp", "frame_options", "content_type_options", "xss_protection"];
                  headersList.forEach((h) => {
                    if (sec[h as keyof typeof sec] === "missing") {
                      missingHeadersCount++;
                    }
                  });
                }
                return missingHeadersCount > 0 ? (
                  <div className="flex justify-between text-pink-600 dark:text-pink-400">
                    <span>Missing Security Headers (-5 ea × {missingHeadersCount}):</span>
                    <span>-{missingHeadersCount * 5}</span>
                  </div>
                ) : null;
              })()}
              <div className="border-t border-gray-200 dark:border-gray-800/80 my-1 pt-1.5 flex justify-between font-black text-gray-800 dark:text-gray-300">
                <span>Final Health Score:</span>
                <span className={gradeInfo.color}>{score}</span>
              </div>
              <div className="text-[8px] text-gray-500 dark:text-gray-400 pt-1 leading-normal italic">
                * Rules: Starts at 100. Deducts 20 per Critical, 12 per High, 7 per Medium, 3 per Low, and 5 per missing security header. Floor score is 10.
              </div>
            </div>
          )}
        </div>

        {/* Severity stats and distribution bar */}
        <div className="lg:col-span-2 bg-gray-50/70 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-800 rounded-xl p-5 flex flex-col justify-between shadow-sm">
          <div className="space-y-2">
            <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest leading-none">Vulnerability Distribution</p>
            <h3 className="text-sm font-black text-gray-900 dark:text-white tracking-wide">Severity Distribution Map</h3>
          </div>

          {/* Custom Horizontal Stacked Bar */}
          <div className="my-4">
            {totalFindings > 0 ? (
              <div className="space-y-2">
                <div className="w-full h-3.5 bg-gray-200 dark:bg-gray-800 rounded-full flex overflow-hidden border border-gray-200 dark:border-gray-800">
                  {severityStats.CRITICAL > 0 && (
                    <div
                      style={{ width: `${getPercent(severityStats.CRITICAL)}%` }}
                      className="bg-red-500 h-full transition-all"
                      title={`Critical: ${severityStats.CRITICAL}`}
                    />
                  )}
                  {severityStats.HIGH > 0 && (
                    <div
                      style={{ width: `${getPercent(severityStats.HIGH)}%` }}
                      className="bg-orange-500 h-full transition-all"
                      title={`High: ${severityStats.HIGH}`}
                    />
                  )}
                  {severityStats.MEDIUM > 0 && (
                    <div
                      style={{ width: `${getPercent(severityStats.MEDIUM)}%` }}
                      className="bg-yellow-500 h-full transition-all"
                      title={`Medium: ${severityStats.MEDIUM}`}
                    />
                  )}
                  {severityStats.LOW > 0 && (
                    <div
                      style={{ width: `${getPercent(severityStats.LOW)}%` }}
                      className="bg-blue-500 h-full transition-all"
                      title={`Low: ${severityStats.LOW}`}
                    />
                  )}
                  {severityStats.INFO > 0 && (
                    <div
                      style={{ width: `${getPercent(severityStats.INFO)}%` }}
                      className="bg-cyan-500 h-full transition-all"
                      title={`Info: ${severityStats.INFO}`}
                    />
                  )}
                </div>

                {/* Distribution Labels */}
                <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[10px] font-mono font-bold text-gray-700 dark:text-gray-300">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block"></span> CRITICAL: {severityStats.CRITICAL}</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block"></span> HIGH: {severityStats.HIGH}</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-500 inline-block"></span> MEDIUM: {severityStats.MEDIUM}</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block"></span> LOW: {severityStats.LOW}</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-cyan-500 inline-block"></span> INFO: {severityStats.INFO}</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="w-full h-3.5 bg-emerald-50 dark:bg-emerald-500/10 rounded-full flex overflow-hidden border border-emerald-200 dark:border-emerald-500/30">
                  <div className="bg-emerald-500 w-full h-full" />
                </div>
                <div className="text-[10px] text-emerald-700 dark:text-emerald-400 font-bold flex items-center gap-1 font-mono">
                  <ShieldCheck size={12} /> 100% Clean scan results. No security issues detected.
                </div>
              </div>
            )}
          </div>

          {/* Quick Filter buttons */}
          <div className="grid grid-cols-5 gap-2">
            {[
              { key: "CRITICAL", count: severityStats.CRITICAL, style: "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/5 hover:border-red-300 dark:hover:border-red-500/30 border-red-200 dark:border-red-500/15 bg-red-50/50 dark:bg-red-500/5" },
              { key: "HIGH", count: severityStats.HIGH, style: "text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-500/5 hover:border-orange-300 dark:hover:border-orange-500/30 border-orange-200 dark:border-orange-500/15 bg-orange-50/50 dark:bg-orange-500/5" },
              { key: "MEDIUM", count: severityStats.MEDIUM, style: "text-yellow-700 dark:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-500/5 hover:border-yellow-300 dark:hover:border-yellow-500/30 border-yellow-200 dark:border-yellow-500/15 bg-yellow-50/50 dark:bg-yellow-500/5" },
              { key: "LOW", count: severityStats.LOW, style: "text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/5 hover:border-blue-300 dark:hover:border-blue-500/30 border-blue-200 dark:border-blue-500/15 bg-blue-50/50 dark:bg-blue-500/5" },
              { key: "INFO", count: severityStats.INFO, style: "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-500/5 hover:border-gray-300 dark:hover:border-gray-500/30 border-gray-200 dark:border-gray-500/15 bg-gray-50 dark:bg-gray-500/5" }
            ].map((btn) => (
              <button
                key={btn.key}
                onClick={() => onSeverityClick(btn.key)}
                className={`py-1.5 px-1.5 border rounded-lg text-center flex flex-col items-center justify-center transition-all cursor-pointer ${btn.style}`}
              >
                <span className="text-[9px] font-mono uppercase tracking-wider font-extrabold leading-none">{btn.key}</span>
                <span className="text-sm font-black font-mono leading-none mt-1">{btn.count}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid: Details and Headers compliance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Left: General Stats list */}
        <div className="bg-gray-50/70 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-800 rounded-xl p-5 flex flex-col justify-between shadow-sm">
          <div className="space-y-4">
            <h3 className="text-xs font-black text-gray-800 dark:text-gray-300 uppercase tracking-widest border-b border-gray-200 dark:border-gray-800 pb-2 flex items-center gap-2">
              <Info className="w-4 h-4 text-orange-500" />
              Scan Snapshot
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between py-1 border-b border-gray-200 dark:border-gray-700/60">
                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" /> Subdomains
                </span>
                <span className="font-mono text-xs font-black text-gray-900 dark:text-white">{data.subdomains?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-gray-200 dark:border-gray-700/60">
                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" /> Findings
                </span>
                <span className="font-mono text-xs font-black text-orange-600 dark:text-orange-400">{totalFindings}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-gray-200 dark:border-gray-700/60">
                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Radio className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" /> Open Ports
                </span>
                <span className="font-mono text-xs font-black text-gray-900 dark:text-white">{data.ports?.length || 0}</span>
              </div>
              <div className="flex items-center justify-between py-1 border-b border-gray-200 dark:border-gray-700/60">
                <span className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Server className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" /> Fingerprints
                </span>
                <span className="font-mono text-xs font-black text-gray-900 dark:text-white">{data.server_fingerprint?.length || 0}</span>
              </div>
            </div>
          </div>

        </div>

        {/* Right: Security Header compliance Checklist details */}
        <div className="md:col-span-2 bg-gray-50/70 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-800 rounded-xl p-5 shadow-sm space-y-4">
          <h3 className="text-xs font-black text-gray-800 dark:text-gray-300 uppercase tracking-widest border-b border-gray-200 dark:border-gray-800 pb-2 flex items-center gap-2">
            <Server className="w-4 h-4 text-orange-500" />
            Security Header Compliance
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {headers.map((h) => (
              <div
                key={h.key}
                className={`p-3 border rounded-xl flex flex-col justify-between transition-all duration-200 ${h.status
                  ? "bg-white dark:bg-emerald-500/[0.02] border-emerald-200 dark:border-emerald-500/10 hover:border-emerald-400 dark:hover:border-emerald-500/25"
                  : "bg-white dark:bg-red-500/[0.02] border-red-200 dark:border-red-500/10 hover:border-red-400 dark:hover:border-red-500/25"
                  }`}
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-gray-900 dark:text-gray-200 tracking-wide">{h.name}</span>
                    {h.status ? (
                      <span className="text-emerald-700 dark:text-emerald-400 flex items-center gap-0.5 text-[10px] font-black uppercase tracking-wider bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 px-1.5 py-0.2 rounded-full">
                        <CheckCircle2 size={10} /> Active
                      </span>
                    ) : (
                      <span className="text-red-700 dark:text-red-400 flex items-center gap-0.5 text-[10px] font-black uppercase tracking-wider bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 px-1.5 py-0.2 rounded-full">
                        <XCircle size={10} /> Missing
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 mt-1 font-mono tracking-tight leading-tight truncate" title={h.fullName}>
                    {h.fullName}
                  </p>
                  <p className="text-[10px] text-gray-600 dark:text-gray-300 font-medium leading-relaxed mt-2">
                    {h.description}
                  </p>
                </div>

                {!h.status && (
                  <div className="mt-3 pt-2 border-t border-red-100 dark:border-red-500/10 text-[9px] text-orange-600 dark:text-orange-400 font-semibold flex items-center gap-1">
                    <AlertTriangle size={10} /> Recommendation available
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
